import React, { useMemo, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { Card } from '@frontend/shadcn/components/ui/card';
import { ChevronLeftIcon, ChevronRightIcon } from '@revfluence/fresh-icons/solid';
import { DiscoverInstagramCreatorsQuery, InstagramCreator } from '@frontend/gql/social/graphql';
import type { Row } from '@tanstack/react-table';
import {
  TableHeader,
  TableHeaderSection,
  TableHeaderLayout,
  TableHeaderLabel,
  TableHeaderIconButton,
} from '../../Analytics/components/table-header';
import { ColumnDefinitionInput } from '../components/table/types';
import { InstagramCreatorSearchTable } from './components/table';
import { useInstagramCreatorSearchTable } from './components/table/hooks/useInstagramCreatorSearchTable';
import { Spinner } from '../../Analytics/components/ui/spinner';
import { useInstagramCreatorOverview } from './hooks/use-instagram-creator-overview';
import { InstagramCreatorOverview } from './instagram-creator-overview';

interface InstagramCreatorSearchTabProps {
  data: DiscoverInstagramCreatorsQuery | undefined;
  clientId: string | undefined | null;
  isLoading: boolean;
  limit: number;
  setSelectedColumns: (
    columns: ColumnDefinitionInput[] | ((prev: ColumnDefinitionInput[]) => ColumnDefinitionInput[]),
  ) => void;
  selectedColumns: ColumnDefinitionInput[];
  onPageChange: (cursor: string | null, direction: 'next' | 'prev') => void;
  isPaginationFetching: boolean;
}

export const InstagramCreatorSearchTab = ({
  data,
  clientId,
  isLoading,
  limit,
  setSelectedColumns,
  selectedColumns,
  onPageChange,
  isPaginationFetching
}: InstagramCreatorSearchTabProps) => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const instagramCreatorsData = useMemo(() => data?.discoverInstagramCreators?.creators ?? [], [data]);

  const paging = data?.discoverInstagramCreators?.paging ?? { cursors: { after: null, before: null }, hasNext: false, hasPrevious: false };
  const { cursors = { after: null, before: null }, hasNext = false, hasPrevious = false } = paging;
  const { after = null, before = null } = cursors;
  const totalCount = instagramCreatorsData.length;

  const creatorId = searchParams.get('creatorId');
  const creatorUsername = searchParams.get('creatorUsername');

  // Define the handler for when the sheet requests to close
  const handleSheetClose = () => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete('creatorId');
    newSearchParams.delete('creatorUsername');
    history.replace({ search: newSearchParams.toString() });
  };

  const { instagramCreatorOverviewProps } = useInstagramCreatorOverview({
    creatorId,
    creatorUsername,
    clientId,
    onSheetShouldClose: handleSheetClose, // Pass the handler to the hook
  });

  const processCreatorClick = useCallback((creator: InstagramCreator) => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('creatorId', creator.id);
    if (creator.username) {
      newSearchParams.set('creatorUsername', creator.username);
    }
    history.push({ search: newSearchParams.toString() });
  }, [location.search, history]);

  const handleRowClick = useCallback((creator: InstagramCreator) => {
    processCreatorClick(creator);
  }, [processCreatorClick]);

  const { instagramTableProps } = useInstagramCreatorSearchTable({
    selectedColumns,
    setSelectedColumns,
    data: instagramCreatorsData,
    currentPage: limit,
    pageSize: 10,
    handleSortingChange: () => { console.log('handleSortingChange'); },
    sorting: [],
    globalFilter: '',
    setIsPartialLoading: () => { console.log('setIsPartialLoading'); },
    tableBodyTableRowProps: useCallback((row: Row<InstagramCreator>) => ({
      onClick: () => handleRowClick(row.original),
    }), [handleRowClick]),
  });

  const handlePrevPage = useCallback(() => {
    if (before) {
      onPageChange(before, 'prev');
    }
  }, [before, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (after) {
      onPageChange(after, 'next');
    }
  }, [after, onPageChange]);

  return (
    <>
      {isLoading && !isPaginationFetching ? (
        <div className="mx-4 mb-4">
          <Skeleton className="h-[110px] rounded-xl mb-4" aria-label="Loading creator search" />
          <Skeleton className="h-[400px] rounded-xl" aria-label="Loading results table" />
        </div>
      ) : (
        <Card className="mx-4 mb-4" aria-label="Instagram Creators Search table">
          <TableHeader>
            <TableHeaderLayout>
              <TableHeaderSection>
                <TableHeaderLabel>
                  <div className="flex items-center gap-2">
                    <span aria-live="polite">{totalCount} Creators</span>
                    {(isLoading || isPaginationFetching) && <Spinner size="sm" spinning={isLoading || isPaginationFetching} />}
                  </div>
                </TableHeaderLabel>
              </TableHeaderSection>
              <TableHeaderSection>
                <nav aria-label="Table navigation" className="flex items-center">
                  <TableHeaderIconButton
                    onClick={handlePrevPage}
                    disabled={!hasPrevious || isPaginationFetching || isLoading}
                    data-dd-action-name="members-table-previous-page"
                    aria-label="Previous page"
                    aria-disabled={!hasPrevious || isPaginationFetching || isLoading}
                  >
                    <ChevronLeftIcon aria-hidden="true" />
                  </TableHeaderIconButton>
                  <TableHeaderIconButton
                    onClick={handleNextPage}
                    disabled={!hasNext || isPaginationFetching || isLoading}
                    data-dd-action-name="members-table-next-page"
                    aria-label="Next page"
                    aria-disabled={!hasNext || isPaginationFetching || isLoading}
                  >
                    <ChevronRightIcon aria-hidden="true" />
                  </TableHeaderIconButton>
                </nav>
              </TableHeaderSection>
            </TableHeaderLayout>
          </TableHeader>
          {
            isPaginationFetching ? (
              <div className="mx-4 mb-4">
                <Skeleton className="h-[400px] rounded-xl" aria-label="Loading results table" />
              </div>
            ) : (
              <InstagramCreatorSearchTable
                {...instagramTableProps}
                updatingColumns={[]}
                aria-busy={isLoading}
                aria-label="Members data table"
              />
            )
          }
        </Card>
      )}
      <InstagramCreatorOverview {...instagramCreatorOverviewProps} />
    </>
  );
};
